import { useState, useEffect, useMemo } from 'react';
import { useEmailContext } from '../../../../../../contexts/EmailContext';
import { datadogLogs } from '@datadog/browser-logs';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot } from 'lexical';
import ChevronIcon from '../../../../Icons/ChevronIcon';
import { QuickRepliesComponentProps } from '../../../../Pages/Common/QuickRepliesContainer';
import { Box, Typography } from '@coolblue-development/freddy';

const EmailQuickReplies = ({ setShowReplies, quickReplies }: QuickRepliesComponentProps): JSX.Element => {
  const { selectedEmail } = useEmailContext();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<connect.QuickResponse | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [editor] = useLexicalComposerContext();

  const filteredReplies = useMemo(() => {
    return selectedCategory ? quickReplies.filter(reply => reply.description === selectedCategory) : quickReplies;
  }, [quickReplies, selectedCategory]);

  useEffect(() => {
    const nonEmptyDescriptions = quickReplies
      .map(reply => reply.description)
      .filter((description): description is string => Boolean(description));

    const uniqueSortedCategories = Array.from(new Set(nonEmptyDescriptions)).sort();

    setCategories(uniqueSortedCategories);
  }, [quickReplies]);

  const handleCategoryClick = (category: string): void => {
    setSelectedCategory(category);
    setShowTemplates(true);
  };

  const handleBackClick = (): void => {
    setShowTemplates(false);
    setSelectedCategory(null);
    setSelectedTemplate(null);
  };

  const handleTemplateSelect = (reply: connect.QuickResponse): void => {
    setSelectedTemplate(reply);
  };

  const handleReplyClick = (reply: connect.QuickResponse): void => {
    editor.update(() => {
      const root = $getRoot();

      const quickReplyText = reply.contents.plainText.content;

      root.selectEnd().insertText(quickReplyText);
    });
    setShowReplies(false);

    datadogLogs.logger.info(`Quick Reply clicked`, {
      reply: reply.name,
      selectedEmail: selectedEmail,
      channel: 'email'
    });
  };

  return (
    <Box
      data-testid="email-quick-replies"
      sx={{
        position: 'absolute',
        bottom: 55,
        border: '1px solid #DDDDDD',
        padding: '15px',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0px 4px 4px 0px #00000040',
        display: 'flex',
        zIndex: 1
      }}
    >
      <Box
        sx={{
          width: '290px',
          flexShrink: 0,
          position: 'relative'
        }}
      >
        <Box sx={{ visibility: showTemplates ? 'hidden' : 'visible', overflowY: 'auto' }}>
          <Typography sx={{ marginBottom: '10px' }} variant="h6">
            Quick Replies
          </Typography>
          {categories.map((category, index) => (
            <Box key={index}>
              <Typography
                sx={{
                  background: '#ffffff',
                  padding: '12px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  '&:hover': { background: '#F2F7FC' }
                }}
                onClick={() => handleCategoryClick(category)}
              >
                <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                  {category}
                </Box>
                <ChevronIcon rotation={180} color="#868A90" />
              </Typography>
            </Box>
          ))}
        </Box>
        {showTemplates && (
          <Box sx={{ position: 'absolute', inset: 0, overflowY: 'auto' }}>
            <Typography
              data-testid="email-quick-replies-back"
              sx={{
                marginBottom: '10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                '&:hover': { color: '#0090E3' }
              }}
              variant="h6"
              onClick={handleBackClick}
            >
              <ChevronIcon rotation={0} color="#868A90" />
              {selectedCategory}
            </Typography>
            {filteredReplies.map((reply, index) => (
              <Typography
                data-testid={`email-quick-reply-${index}`}
                key={index}
                sx={{
                  padding: '12px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  background: selectedTemplate === reply ? '#F2F7FC' : '#ffffff',
                  '&:hover': { background: '#F2F7FC' },
                  '&&': {
                    margin: '0px'
                  }
                }}
                onClick={() => handleTemplateSelect(reply)}
              >
                Template {index + 1}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
      {selectedTemplate && (
        <Box
          sx={{
            width: '272px',
            marginLeft: '15px',
            flexShrink: 0,
            position: 'relative'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              padding: '20px',
              borderRadius: '4px',
              overflow: 'auto',
              cursor: 'pointer',
              '&:hover': { background: '#F2F7FC' }
            }}
            onClick={() => handleReplyClick(selectedTemplate)}
          >
            <Typography>{selectedTemplate.contents.plainText.content}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EmailQuickReplies;
